import { isValidPhoneNumber } from "react-phone-number-input";
import { parsePhoneNumber } from "libphonenumber-js/max";

export const PHONE_ERROR_MOBILE = "Please enter a valid mobile number";
export const PHONE_ERROR_GENERIC = "Please enter a valid phone number";

/** Ofcom UK drama / fictional ranges (national digits, no spaces). */
const OFCOM_FICTIONAL = [
  /^07700900\d{3}$/, // 07700 900000–900999
  /^08081570\d{3}$/, // 08081 570000–570999
  /^01632960\d{3}$/, // 01632 960000–960999
  /^0306999\d{4}$/, // 0306 999 0000–0999
  /^02079460\d{3}$/, // 020 7946 0000–0999
];

export function phoneErrorMessage(mobileOnly = true) {
  return mobileOnly ? PHONE_ERROR_MOBILE : PHONE_ERROR_GENERIC;
}

export function digitsOf(value) {
  return String(value || "").replace(/\D/g, "");
}

/**
 * Convert a stored or typed value to E.164 when possible.
 * National UK numbers without a + are parsed as GB.
 */
export function toE164(value, defaultCountry = "GB") {
  const raw = String(value || "").trim();
  if (!raw) return "";
  const digits = digitsOf(raw);
  const candidates = [raw];
  if (raw.startsWith("+")) {
    candidates.push(raw);
  } else if (digits.startsWith("44") && digits.length >= 12) {
    candidates.push(`+${digits}`);
  } else {
    candidates.push(raw);
  }
  for (const candidate of candidates) {
    try {
      const parsed = parsePhoneNumber(
        candidate,
        candidate.startsWith("+") ? undefined : defaultCountry
      );
      if (parsed?.number) return parsed.number;
    } catch {
      // try next
    }
  }
  return raw.startsWith("+") ? raw : "";
}

function nationalDigits(value) {
  try {
    const parsed = parsePhoneNumber(String(value || ""), String(value || "").startsWith("+") ? undefined : "GB");
    if (parsed) {
      const national = parsed.formatNational();
      const digits = digitsOf(national);
      if (parsed.country === "GB" && digits && !digits.startsWith("0")) return `0${digits}`;
      return digits;
    }
  } catch {
    // fall through
  }
  const digits = digitsOf(value);
  if (digits.startsWith("44") && digits.length >= 12) return `0${digits.slice(2)}`;
  return digits;
}

export function isRepeatedDigits(value) {
  const digits = nationalDigits(value).replace(/^0+/, "");
  return digits.length >= 6 && /^(\d)\1+$/.test(digits);
}

export function isSequentialDigits(value) {
  const candidates = [nationalDigits(value), nationalDigits(value).replace(/^0+/, "")];
  return candidates.some((digits) => {
    if (digits.length < 8) return false;
    let asc = true;
    let desc = true;
    for (let i = 1; i < digits.length; i += 1) {
      const prev = Number(digits[i - 1]);
      const next = Number(digits[i]);
      if ((prev + 1) % 10 !== next) asc = false;
      if ((prev + 9) % 10 !== next) desc = false;
    }
    return asc || desc;
  });
}

export function isOfcomFictional(value) {
  const national = nationalDigits(value);
  const plus44 = national.startsWith("0") ? `44${national.slice(1)}` : national;
  const e164Digits = digitsOf(toE164(value) || value);
  return OFCOM_FICTIONAL.some(
    (re) => re.test(national) || re.test(`0${plus44.replace(/^44/, "")}`) || re.test(`0${e164Digits.replace(/^44/, "")}`)
  );
}

export function isFakePhoneNumber(value) {
  if (!value) return false;
  return isRepeatedDigits(value) || isSequentialDigits(value) || isOfcomFictional(value);
}

/**
 * @param {string} value
 * @param {{ mobileOnly?: boolean, required?: boolean, defaultCountry?: string }} [options]
 * @returns {{ ok: boolean, value: string, reason: string | null }}
 */
export function validatePhone(value, options = {}) {
  const { mobileOnly = true, required = true, defaultCountry = "GB" } = options;
  const raw = String(value || "").trim();

  if (!raw) {
    return { ok: !required, value: "", reason: required ? "empty" : null };
  }

  const e164 = toE164(raw, defaultCountry);
  const candidate = e164 || raw;

  if (isFakePhoneNumber(candidate) || isFakePhoneNumber(raw)) {
    const reason = isOfcomFictional(candidate) || isOfcomFictional(raw)
      ? "fictional"
      : isRepeatedDigits(candidate) || isRepeatedDigits(raw)
        ? "repeated"
        : "sequential";
    return { ok: false, value: candidate, reason };
  }

  if (!isValidPhoneNumber(candidate)) {
    return { ok: false, value: candidate, reason: "invalid" };
  }

  if (mobileOnly) {
    try {
      const parsed = parsePhoneNumber(candidate);
      const type = parsed?.getType?.();
      if (type !== "MOBILE") {
        return { ok: false, value: candidate, reason: "not_mobile" };
      }
    } catch {
      return { ok: false, value: candidate, reason: "invalid" };
    }
  }

  return { ok: true, value: candidate, reason: null };
}

export function isValidPhone(value, options = {}) {
  return validatePhone(value, options).ok;
}

export function arePhonesValid(entries = []) {
  return entries.every((entry) => {
    if (!entry) return true;
    const { value, mobileOnly = true, required = true } = entry;
    return validatePhone(value, { mobileOnly, required }).ok;
  });
}

/** Collect phone fields from application edit/new forms for bulk validation. */
export function applicationFormPhones(borrowerType, form = {}) {
  const t = String(borrowerType || "").toLowerCase();
  if (t.includes("limited") || t.includes("ltd") || t.includes("llp")) {
    return [
      { value: form.company?.phone, mobileOnly: false },
      ...(form.applicants || []).map((a, i) => ({ value: a.phone, required: i === 0 })),
    ];
  }
  if (t.includes("partner")) {
    return (form.partners || []).map((p, i) => ({ value: p.phone, required: i === 0 }));
  }
  if (t.includes("pension") || t.includes("trust")) {
    return (form.members || []).map((m, i) => ({ value: m.phone, required: i === 0 }));
  }
  return [{ value: form.phone }];
}
